import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PanelDetailsView from "./PanelDetailsView";
import {
  RootStateServiceLogicBuilder,
  serviceLogicBuilderActions,
} from "../../store/service-logic-builder-slice";
import PageLoader from "../../../../common/ui/PageLoader";
import NewPanelModal from "./NewPanelModal";
import {
  deleteUserLabPanel,
  getProviderLabPanels,
} from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";
import { KNOWN_PROVIDERS, normalizeProviderPanel } from "./SavedPanelsScreen";

const PanelDetailsViewWrapper = () => {
  const { panelId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
    ASGetLabPanelsList,
  } = useAppStore();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [providerLoading, setProviderLoading] = useState(false);

  const savedLabPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLabPanels
  );
  const providerLabPanelsByProvider = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.providerLabPanels
  );

  // Resolve the panel by id from either the user's saved panels (editable) or
  // the cached provider catalogs (read-only). Ids are unique across both.
  const userPanel = useMemo(
    () => savedLabPanels.find((p: any) => p.id === panelId),
    [savedLabPanels, panelId]
  );

  const providerPanel = useMemo(() => {
    for (const provider of KNOWN_PROVIDERS) {
      const found = providerLabPanelsByProvider[provider]?.find(
        (p: any) => p.id === panelId
      );
      if (found) return found;
    }
    return null;
  }, [providerLabPanelsByProvider, panelId]);

  const panel = userPanel ?? providerPanel;
  const isProviderPanel = !userPanel && !!providerPanel;

  const allProvidersLoaded = KNOWN_PROVIDERS.every(
    (provider) => providerLabPanelsByProvider[provider] !== undefined
  );

  // Not a saved user panel and not yet resolved from cache: fetch the provider
  // catalogs we haven't loaded yet (already-cached ones, e.g. from the listing,
  // are not refetched) so the panel can be found by id.
  useEffect(() => {
    if (userPanel || providerPanel || allProvidersLoaded) return;

    const missing = KNOWN_PROVIDERS.filter(
      (provider) => providerLabPanelsByProvider[provider] === undefined
    );
    if (missing.length === 0) return;

    let cancelled = false;
    setProviderLoading(true);
    Promise.all(
      missing.map((provider) =>
        getProviderLabPanels(provider)
          .then((response) => ({
            provider,
            panels: Array.isArray(response)
              ? response.map(normalizeProviderPanel)
              : [],
          }))
          .catch(() => ({ provider, panels: [] as any[] }))
      )
    )
      .then((results) => {
        if (cancelled) return;
        results.forEach(({ provider, panels }) =>
          dispatch(
            serviceLogicBuilderActions.setProviderLabPanels({
              provider,
              panels,
            })
          )
        );
      })
      .finally(() => {
        // Always clear loading: this effect re-runs when the cache updates,
        // which flips `cancelled` for the prior run; guarding here would leave
        // the flag stuck true.
        setProviderLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    userPanel,
    providerPanel,
    allProvidersLoaded,
    providerLabPanelsByProvider,
    dispatch,
  ]);

  // Once saved panels and every known provider are loaded and the id matched
  // nothing, return to the list.
  useEffect(() => {
    if (panel) return;
    const providersResolved = allProvidersLoaded && !providerLoading;
    const savedResolved = savedLabPanels.length > 0;
    if (providersResolved && savedResolved) {
      navigate("/client/services/lab");
    }
  }, [
    panel,
    allProvidersLoaded,
    providerLoading,
    savedLabPanels.length,
    navigate,
  ]);

  const handleDelete = () => {
    ASSetSnackBarMessage({
      title: "Delete panel template?",
      message: "This will not affect existing orders using this template.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        if (!panel?.id) return;

        // Close modal and navigate back immediately
        setEditModalOpen(false);
        navigate("/client/services/lab");
        dispatch(serviceLogicBuilderActions.setLoading(true));

        try {
          await deleteUserLabPanel(panel.id);
          await ASGetLabPanelsList();

          ASSetSnackBarMessage({
            title: "Success",
            message: `Panel "${panel.name}" deleted successfully!`,
          });
          ASSetTypeSnackBar("success");
          ASSetOpenSnackBar(true);
        } catch (error) {
          ASSetSnackBarMessage({
            title: "Error",
            message: "Failed to delete panel. Please try again.",
          });
          ASSetTypeSnackBar("alert");
          ASSetOpenSnackBar(true);
        } finally {
          dispatch(serviceLogicBuilderActions.setLoading(false));
        }
      },
      onCancel: () => {},
    });
    ASSetOpenSnackBar(true);
  };

  const handlePanelSaved = async () => {
    // Refresh only the lab panels — no need to refetch services/logics too.
    await ASGetLabPanelsList();
  };

  if (!panel) {
    return <PageLoader />;
  }

  return (
    <>
      <PanelDetailsView
        panel={panel}
        onEdit={() => setEditModalOpen(true)}
        onDelete={handleDelete}
        hideActions={isProviderPanel}
      />
      {/* The edit modal is unreachable while the header action deletes instead
          of editing (nothing sets editModalOpen), but it stays mounted so
          re-enabling edit is a one-line change in PanelDetailsView. */}
      {!isProviderPanel && (
        <NewPanelModal
          openModal={editModalOpen}
          handleClose={() => setEditModalOpen(false)}
          onPanelSaved={handlePanelSaved}
          editingPanel={panel}
          onDelete={handleDelete}
        />
      )}
    </>
  );
};

export default PanelDetailsViewWrapper;
